import { ApiErrorSchema, type ErrorCode, type ErrorParams } from "@contract";

/** A non-2xx API response, already reduced to its machine code and params. */
export class ApiRequestError extends Error {
  readonly code: ErrorCode | "NETWORK";
  readonly params: ErrorParams | undefined;
  readonly status: number;

  constructor(code: ErrorCode | "NETWORK", status: number, params?: ErrorParams) {
    super(code);
    this.name = "ApiRequestError";
    this.code = code;
    this.params = params;
    this.status = status;
  }

  /** i18next key: errors:CODE. */
  get i18nKey(): string {
    return `errors:${this.code}`;
  }
}

export interface FetcherOptions {
  readonly method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  readonly body?: unknown;
  /** Do not redirect to /signin on 401 (used by the sign-in screen itself). */
  readonly allowUnauthenticated?: boolean;
  readonly signal?: AbortSignal;
}

let onUnauthenticated: () => void = () => {
  if (window.location.pathname !== "/signin") window.location.assign("/signin");
};

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

/**
 * Calls /api with the session cookie; throws ApiRequestError on any non-2xx,
 * on a network failure (NETWORK), and on a 2xx whose body is not JSON
 * (INTERNAL: a proxy error page must not surface as a raw SyntaxError).
 */
export async function api<T>(path: string, options: FetcherOptions = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method: options.method ?? "GET",
      credentials: "include",
      headers: options.body === undefined ? {} : { "content-type": "application/json" },
      body: options.body === undefined ? null : JSON.stringify(options.body),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch {
    throw new ApiRequestError("NETWORK", 0);
  }
  if (response.ok) {
    try {
      return (await response.json()) as T;
    } catch {
      throw new ApiRequestError("INTERNAL", response.status);
    }
  }
  const parsed = ApiErrorSchema.safeParse(await response.json().catch(() => null));
  const code: ErrorCode = parsed.success ? parsed.data.error.code : "INTERNAL";
  const params = parsed.success ? parsed.data.error.params : undefined;
  if (response.status === 401 && !options.allowUnauthenticated) onUnauthenticated();
  throw new ApiRequestError(code, response.status, params);
}

/**
 * How long an upload may take before the transport gives up. A 32 MB Source on
 * a slow line is minutes, so this is a backstop against a connection that
 * stalls forever rather than a deadline; the Author's own Cancel is the quick
 * way out.
 */
export const UPLOAD_TIMEOUT_MS = 10 * 60_000;

export interface UploadOptions {
  /**
   * Fraction 0..1 of the body sent so far, or null while the transport gives
   * no total. Called at least once with null before the first byte, so the
   * caller can show a busy state on a transport without progress events.
   */
  readonly onProgress?: (fraction: number | null) => void;
  readonly signal?: AbortSignal;
  /** Transport deadline in ms; UPLOAD_TIMEOUT_MS when not given. */
  readonly timeoutMs?: number;
}

/**
 * Multipart POST to /api with the session cookie, over XMLHttpRequest rather
 * than fetch: only XHR reports how much of the body has gone out, and an
 * upload of a 32 MB Source with no progress is a dead screen. The error
 * mapping is the same as api(): a machine code, never a body.
 */
export function upload<T>(path: string, form: FormData, options: UploadOptions = {}): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("POST", `/api${path}`);
    request.withCredentials = true;
    request.responseType = "text";
    request.timeout = options.timeoutMs ?? UPLOAD_TIMEOUT_MS;
    options.onProgress?.(null);
    request.upload.addEventListener("progress", (event) => {
      options.onProgress?.(
        event.lengthComputable && event.total > 0 ? event.loaded / event.total : null,
      );
    });
    let settled = false;
    const settle = (fail: ApiRequestError | null, value?: T) => {
      if (settled) return;
      settled = true;
      options.signal?.removeEventListener("abort", abort);
      if (fail) reject(fail);
      else resolve(value as T);
    };
    // A cancelled upload settles here rather than waiting for the transport's
    // own abort event, so the caller is never left holding a promise that a
    // half-closed connection never resolves.
    function abort() {
      request.abort();
      settle(new ApiRequestError("NETWORK", 0));
    }
    options.signal?.addEventListener("abort", abort, { once: true });
    request.addEventListener("error", () => settle(new ApiRequestError("NETWORK", 0)));
    request.addEventListener("timeout", () => settle(new ApiRequestError("NETWORK", 0)));
    request.addEventListener("abort", () => settle(new ApiRequestError("NETWORK", 0)));
    request.addEventListener("load", () => {
      const status = request.status;
      const parsed = parseJson(request.responseText);
      if (status >= 200 && status < 300) {
        if (parsed === undefined) settle(new ApiRequestError("INTERNAL", status));
        else settle(null, parsed as T);
        return;
      }
      const error = ApiErrorSchema.safeParse(parsed);
      const code: ErrorCode = error.success ? error.data.error.code : "INTERNAL";
      if (status === 401) onUnauthenticated();
      settle(
        new ApiRequestError(code, status, error.success ? error.data.error.params : undefined),
      );
    });
    request.send(form);
  });
}

/** A response body as JSON, or undefined when it is not JSON at all (a proxy error page). */
function parseJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

/**
 * The error code to render for anything a query or a mutation threw. Anything
 * that is not an ApiRequestError (a bug in a component, a rejected promise
 * from somewhere else) is INTERNAL, because the Author must never see a raw
 * JavaScript message.
 */
export const errorCodeOf = (error: unknown): ErrorCode | "NETWORK" =>
  error instanceof ApiRequestError ? error.code : "INTERNAL";
